import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import GroupsIcon from '@mui/icons-material/Groups';
import UserAvatar from '@/components/UserAvatar';

export interface GroupMemberRow {
  user_id: number;
  reference_no: string;
  full_name?: string | null;
  photo_path?: string | null;
  photo_url?: string | null;
  role: string;
  group_role: string;
}

interface Props {
  title: string;
  members: GroupMemberRow[];
  accent: 'info' | 'secondary';
  canManage: boolean;
  ownerUserId?: number;
  onAdd: () => void;
  onRemove: (userId: number) => void;
  onRoleChange: (userId: number, role: 'manager' | 'participant') => void;
}

export default function GroupMemberRoleCard({
  title,
  members,
  accent,
  canManage,
  ownerUserId,
  onAdd,
  onRemove,
  onRoleChange,
}: Props) {
  const Icon = accent === 'info' ? AdminPanelSettingsIcon : GroupsIcon;

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        height: '100%',
        borderColor: (t) => alpha(t.palette[accent].main, 0.35),
        background: (t) =>
          `linear-gradient(180deg, ${alpha(t.palette[accent].main, t.palette.mode === 'dark' ? 0.12 : 0.06)} 0%, ${t.palette.background.paper} 100%)`,
      }}
    >
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: (t) => alpha(t.palette[accent].main, 0.15),
                color: (t) => t.palette[accent].main,
              }}
            >
              <Icon fontSize="small" />
            </Avatar>
            <Box>
              <Typography variant="subtitle1" fontWeight={800}>
                {title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {members.length} 人
              </Typography>
            </Box>
          </Stack>
          {canManage && (
            <Button size="small" variant="outlined" color={accent} startIcon={<AddIcon />} onClick={onAdd}>
              新增
            </Button>
          )}
        </Stack>

        {members.length === 0 ? (
          <Box sx={{ py: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              尚未加入{title.replace(/\s*\(\d+\)$/, '')}
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1}>
            {members.map((m) => {
              const isOwner = ownerUserId != null && m.user_id === ownerUserId;
              return (
                <Box
                  key={m.user_id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    p: 1,
                    borderRadius: 2,
                    bgcolor: 'action.hover',
                  }}
                >
                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                    <UserAvatar
                      photoUrl={m.photo_url}
                      photoPath={m.photo_path}
                      referenceNo={m.reference_no}
                      fullName={m.full_name}
                      sx={{ width: 36, height: 36, fontSize: '0.9rem' }}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                        <Typography fontWeight={700} noWrap variant="body2">
                          {m.full_name || m.reference_no}
                        </Typography>
                        {isOwner && (
                          <Chip size="small" label="群主" color="secondary" sx={{ height: 20 }} />
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {m.reference_no}
                      </Typography>
                    </Box>
                  </Stack>
                  {canManage && !isOwner && (
                    <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
                      <TextField
                        select
                        size="small"
                        value={m.group_role}
                        onChange={(e) => onRoleChange(m.user_id, e.target.value as 'manager' | 'participant')}
                        sx={{ minWidth: 96 }}
                      >
                        <MenuItem value="manager">管理員</MenuItem>
                        <MenuItem value="participant">參與者</MenuItem>
                      </TextField>
                      <IconButton size="small" color="error" onClick={() => onRemove(m.user_id)}>
                        <PersonRemoveIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
